import React, { useCallback, useMemo, useState } from "react";
import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { aggridHeaderClass, SmallTitleWithSubSpan, SmallTitleSubRow, SummaryChip } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { CreateCellClickHandler, CustomCellClickAction, createCellCssClassRules, postSortHandler, RemoveRowHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { HRM3130_R01TaxTableDto, defaultHRM3130_R01TaxTableDto } from "./Dto_HRM3130_R01";
import { useHRM3130_R01Store } from "./Store_HRM3130_R01";
import { headerNames_HRM3130_R01, HRM3130_R01_TAX_FIELDS, formatHRM3130_R01Amount } from "./Util_HRM3130_R01";
import { useHRM3130_R01CellChange } from "./hooks/HRM3130_R01CellChange";
import { showWarning } from "src/utils/toast/toastUtil";
const COLUMN_STATE_KEY = 'HRM3130_R01-grid-column-state'; // 화면 배치 보관 키
/** 그리드와 Content 사이에 공유할 참조·삽입 동작이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM3130_R01TaxTableDto> | null>; // 편집·이동 대상
    handleInsertClick: (event: React.MouseEvent, rowIndex?: number, setClickable?: (value: boolean) => void) => void; // 공통 + 버튼 동작
}
/** HRM2130 편집 그리드 조합으로 간이세액표와 미리보기 요약을 반환한다. */
export const Table_HRM3130_R01 = ({ gridRef, handleInsertClick }: Props) => {
    const state = useHRM3130_R01Store(); // 현재 목록·선택·배치 구독
    const { HRM3130_R01List: list, originalHRM3130_R01List: original, selectedHRM3130_R01List: selected, deletedHRM3130_R01List: deleted, columnState, setColumnState, scrollTop, setScrollTop } = state; // 그리드 상태
    const [clickable, setClickable] = useState(true); // true이면 일반 행 클릭을 허용한다.
    const selectedNumbers = useMemo(() => selected.map(Number), [selected]); // 공통 CSS·훅에서 숫자로 비교
    const deletedNumbers = useMemo(() => deleted.map(Number), [deleted]); // 공통 CSS·훅에서 숫자로 비교
    const { getRowId, createRowClassRules, createCellClassRules, paddedGridData, renderIndexCell } = useGridSettingContent<HRM3130_R01TaxTableDto>({
        gridRef, key: 'valueIndex', gridData: list, defaultGridData: defaultHRM3130_R01TaxTableDto,
        selectedRow: selected, originalGridData: original, updateRow: state.updateHRM3130_R01List,
    }); // 기준 Table의 행 ID·패딩·index 렌더러 사용
    const cssColumns = useMemo(() => createCellCssClassRules({
        disabledCells: [], insertedDisabledCells: [], commonDisabledCells: ['fromAmt'],
        pointerCells: ['toAmt', ...HRM3130_R01_TAX_FIELDS, 'remark'], insertedPointerCells: ['fromAmt'], commonPointerCells: [],
        alignLeftCells: ['remark'],
    }), []); // 신규 행에서만 시작 금액을 편집하는 스타일
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM3130_R01TaxTableDto>({
            idKey: 'valueIndex', selectedList: selectedNumbers, deletedList: deletedNumbers, ...cssColumns,
        }),
    }), [createCellClassRules, selectedNumbers, deletedNumbers, cssColumns]); // 하단 고정 행 가운데 정렬도 공통 규칙 사용
    const editableColumns = useMemo(() => ['fromAmt', 'toAmt', ...HRM3130_R01_TAX_FIELDS, 'remark'], []); // 편집 탐색 순서
    const baseColumnDefs = useMemo<ColDef<HRM3130_R01TaxTableDto>[]>(() => [
        ...getDefaultRowIndexColumnConfig(params => renderIndexCell(params,
            inner => getRowStyleFlags(inner, { deletedList: deletedNumbers as unknown as string[], selectedList: selectedNumbers as unknown as string[], dataList: list }),
            (rowIndex, event) => handleInsertClick(event, rowIndex, setClickable),
            row => RemoveRowHandler(gridRef, list, row, setClickable, state.removeHRM3130_R01List, state.addDeletedHRM3130_R01List))),
        ...(['fromAmt', 'toAmt', ...HRM3130_R01_TAX_FIELDS] as const).map(field => ({
            headerName: headerNames_HRM3130_R01[field], colId: field, field, width: field.startsWith('tax') ? 84 : 112,
            editable: params => !params.node.rowPinned && !!params.data && (field !== 'fromAmt' || params.data.inserted),
            cellDataType: false,
            /**
             * 입력 문자열을 숫자로 바꿔 반환한다. 숫자가 아니거나 빈칸이면 경고하고 이전 값을 그대로 반환한다.
             * 세액 표라 실수로 0이나 NaN이 들어가면 안 되므로, 기준 화면(HRM2130)의 "숫자가 아니면 0" 대신 이전 값을 유지한다(사용자 결정).
             * 이전 값과 같은 값을 반환하면 그리드가 값 변경 이벤트를 만들지 않아 수정 표시(isModified)도 생기지 않는다.
             * 음수는 여기서 통과시키고, 셀 변경 훅이 경고 후 0으로 보정한다.
             */
            valueParser: params => {
                // 쉼표와 앞뒤 공백을 지운 입력값이다. 정규식 /,/g: 문자열 안의 모든 쉼표를 찾는다.
                const text = String(params.newValue ?? '').replace(/,/g, '').trim();
                // 정규식: 앞의 -는 있어도 되고, 숫자가 1개 이상, 소수점 뒤 숫자는 있어도 되는 형식만 숫자로 인정한다.
                if (!/^-?\d+(\.\d+)?$/.test(text)) {
                    showWarning('숫자만 입력할 수 있습니다.');
                    return params.oldValue;
                }
                return Number(text);
            },
            valueFormatter: params => formatHRM3130_R01Amount(params.value),
        } as ColDef<HRM3130_R01TaxTableDto>)),
        { headerName: '비고', colId: 'remark', field: 'remark', width: 300, editable: params => !params.node.rowPinned,
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 1000 } },
    ], [renderIndexCell, deletedNumbers, selectedNumbers, list, handleInsertClick, gridRef, state.removeHRM3130_R01List, state.addDeletedHRM3130_R01List]);
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]); // 기본 배치
    const columnDefs = useMemo(() => {
        if (columnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<HRM3130_R01TaxTableDto>({
            baseColumnDefs, columnState, headerNames: headerNames_HRM3130_R01, fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, columnState]); // PRD2010 방식으로 배치를 복원
    const handleRowClick = useRowSelectionHandler<HRM3130_R01TaxTableDto>({
        dataList: list, idKey: 'valueIndex',
        selectedList: selectedNumbers as unknown as string[], deletedList: deletedNumbers as unknown as string[],
        addSelected: state.addSelectedHRM3130_R01List, removeSelected: state.removeSelectedHRM3130_R01List, clickable, setClickable,
    }); // 합계·삭제 행과 +/- 직후 클릭을 공통 훅에서 제외
    const cellEventHandlers = useDetailTableEvents({ detailList: list, editableColumns, keyDownConfig: { enableArrowNavigation: true } }); // 키보드 탐색
    const cellClickActions = useMemo<CustomCellClickAction<HRM3130_R01TaxTableDto>[]>(() => [{
        validate: (field, data) => editableColumns.includes(field) && (field !== 'fromAmt' || data.inserted),
        action: event => { if (!event.node.rowPinned) event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }); },
    }], [editableColumns]); // 실제 편집 가능 셀만 클릭 편집
    const handleCellClick = CreateCellClickHandler(list, 'valueIndex', cellClickActions); // 공통 셀 클릭 처리
    /** 정렬 시 기존 행과 신규 행의 기준 순서를 공통 함수로 유지한다. */
    const handlePostSort = useCallback((params: PostSortRowsParams<HRM3130_R01TaxTableDto>) => {
        postSortHandler(params, list, original, state.setHRM3130_R01List);
    }, [list, original, state.setHRM3130_R01List]);
    const { handleCellChange } = useHRM3130_R01CellChange({ updateHRM3130_R01List: state.updateHRM3130_R01List }); // 금액 보정
    const pinnedBottomRow = useMemo(() => [{ fromAmt: list.length + '건' }], [list]); // 삭제 표시 포함 실제 목록 건수
    const preview = state.HRM3130_R01IncomeTax; // 서버 계산 결과
    return <>
        {/* 영역 제목은 표시 단위만 안내한다. */}
        <SmallTitleWithSubSpan title="근로소득 간이세액표" subTitle="단위: 원" />
        {/* 저장된 표 계산 결과와 등록 연도 안내를 표시한다. */}
        <SmallTitleSubRow className="gap-1.5 text-xs">
            {preview ? <>
                <SummaryChip label="소득세" value={formatHRM3130_R01Amount(preview.incomeTax)} tone="blue" />
                <SummaryChip label="지방소득세" value={formatHRM3130_R01Amount(preview.localIncomeTax)} tone="blue" />
                {preview.applyYear !== state.HRM3130_R01Filter.applyYear && <span>{preview.applyYear}년 표 기준</span>}
            </> : <span>월 과세급여를 입력하면 저장된 표 기준으로 해당 구간과 세액을 찾습니다.</span>}
            <span className="ml-auto">{state.HRM3130_R01YearList.length ? '등록 연도: ' + state.HRM3130_R01YearList.join(', ') : '등록 연도 없음'}</span>
        </SmallTitleSubRow>
        {/* 공통 컨텍스트 메뉴와 컬럼 배치 보관을 연결한다. */}
        <GridContextWrapper<HRM3130_R01TaxTableDto> enableCellCalculation gridRef={gridRef} paddedGridData={paddedGridData}
            initSelected={state.initSelectedHRM3130_R01List} defaultRowData={defaultHRM3130_R01TaxTableDto}
            allSelected={state.allSelectedHRM3130_R01List} baseColumnDefs={columnDefs} defaultColumnState={defaultColumnState}
            setColumnState={setColumnState} localStorageKey={COLUMN_STATE_KEY} menuItems={undefined}>
            {/* 실제 편집 그리드에 행·셀·키보드·하단 건수 이벤트를 연결한다. */}
            <Grid_Excel<HRM3130_R01TaxTableDto> ref={gridRef} rowData={paddedGridData} getRowId={getRowId}
                columnDefs={columnDefs} defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<HRM3130_R01TaxTableDto>({ idKey: 'valueIndex', selectedList: selectedNumbers, deletedList: deletedNumbers })}
                onRowClicked={handleRowClick} onCellClicked={handleCellClick} onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                onCellValueChanged={handleCellChange} pinnedBottomRowData={pinnedBottomRow} onPostSortRows={handlePostSort}
                columnStateKey={COLUMN_STATE_KEY} setColumnState={setColumnState} getDefaultColumnState={() => defaultColumnState}
                scrollTop={scrollTop} setScrollTop={setScrollTop} />
        </GridContextWrapper>
    </>;
};
